// Home.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/navigation';

// Import Hero
import HomeHero from './HomeHero';

// === IMAGES ===
import sigiriya from '../assets/pick2/sigiriya/istockphoto-1492229422-612x612.jpg';
import udawalawa from '../assets/pick2/udawalawe/istockphoto-904632224-612x612.jpg';
import ella from '../assets/pick2/Ella/pexels-iakub-arifulin-205356454-11675420.jpg';
import mirissa from '../assets/pick2/Mirissa/pexels-ollivves-1005417.jpg';
import nuwaraeliya from '../assets/pick2/Nuware Eliya/pexels-gojo-satoru-2153113567-32560382.jpg';
import kandy from '../assets/pick2/kandy/pexels-gihan-bandara-142954990-20836060.jpg';
import galle from '../assets/pick2/Galle/pexels-shainee-2933943.jpg';
import jaffna from '../assets/pick/33.jpg';
import anuradhapura from '../assets/pick2/Anuradapura/istockphoto-1178654295-612x612.jpg';
import polonnaruwa from '../assets/pick2/polonnaruwa/istockphoto-510311016-612x612.jpg';

import excursionImage from '../assets/pick/34.jpg';
import esalaPeraharaImage from '../assets/pick/35.jpg';
import safariImage from '../assets/pick/37.jpg';
import label1 from '../assets/pick/36.jpg';
import label2 from '../assets/pick2/yala/LK710A2062-02-E.webp';

import package1 from '../assets/pick/38.jpg';
import package2 from '../assets/pick/33.jpg';
import package3 from '../assets/pick2/Anuradapura/istockphoto-1178654295-612x612.jpg';
import package4 from '../assets/pick2/yala/istockphoto-579436668-612x612.jpg';

import aboutContactImage from '../assets/pick/39.jpg';

// === FRAMER MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: "easeOut" } },
};

const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.7 } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const accordionItem = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

// === TITLE COMPONENT ===
const TitleTwoBorder = ({ mainTitle, subTitle }) => (
    <div className="flex flex-col items-center text-center">
        <h2 className="font-konkhmer text-[2rem] uppercase text-white md:text-4xl lg:text-5xl xl:text-6xl">
            <span
                style={{
                    background: 'linear-gradient(to right, #009CE6, #96BA33)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                }}
            >
                {mainTitle}
            </span>
        </h2>
        <p className="mt-2 font-inknut text-sm text-white/80 md:text-base lg:mt-3 lg:text-lg">
            {subTitle}
        </p>
        <div className="mt-3 flex w-full max-w-md gap-2">
            <div className="h-[3px] flex-1" style={{ background: '#009CE6' }}></div>
            <div className="h-[3px] flex-1" style={{ background: '#96BA33' }}></div>
        </div>
    </div>
);

const Home = () => {
    const { t } = useTranslation(); // Initialize translation
    const [activeIndex, setActiveIndex] = useState(null);
    const [activePackage, setActivePackage] = useState(0);
    const [isMd, setIsMd] = useState(false);

    const toggleService = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    // === RECOMMENDATIONS DATA (Translated labels & names) ===
    const recommendations = [
        { label: t('recommendations.sigiriya.label'), name: t('recommendations.sigiriya.name'), image: sigiriya },
        { label: t('recommendations.udawalawa.label'), name: t('recommendations.udawalawa.name'), image: udawalawa },
        { label: t('recommendations.ella.label'), name: t('recommendations.ella.name'), image: ella },
        { label: t('recommendations.mirissa.label'), name: t('recommendations.mirissa.name'), image: mirissa },
        { label: t('recommendations.nuwaraeliya.label'), name: t('recommendations.nuwaraeliya.name'), image: nuwaraeliya },
        { label: t('recommendations.kandy.label'), name: t('recommendations.kandy.name'), image: kandy },
        { label: t('recommendations.galle.label'), name: t('recommendations.galle.name'), image: galle },
        { label: t('recommendations.jaffna.label'), name: t('recommendations.jaffna.name'), image: jaffna },
        { label: t('recommendations.anuradhapura.label'), name: t('recommendations.anuradhapura.name'), image: anuradhapura },
        { label: t('recommendations.polonnaruwa.label'), name: t('recommendations.polonnaruwa.name'), image: polonnaruwa },
    ];

    // === EXCURSIONS DATA ===
    const excursions = [
        {
            title: t('excursions.classic7.title'),
            des: t('excursions.classic7.des'),
        },
        {
            title: t('excursions.northern11.title'),
            des: t('excursions.northern11.des'),
        },
        {
            title: t('excursions.meditation9.title'),
            des: t('excursions.meditation9.des'),
        },
        {
            title: t('excursions.classic9.title'),
            des: t('excursions.classic9.des'),
        },
    ];

    // === PACKAGES DATA ===
    const packages = [
        {
            id: 1,
            image: package1,
            title: t('packages.classic7.title'),
            duration: t('packages.classic7.duration'),
            description: t('packages.classic7.description'),
            location: "Sri Lanka",
            category: t('packages.classic7.category'),
        },
        {
            id: 2,
            image: package2,
            title: t('packages.northern11.title'),
            duration: t('packages.northern11.duration'),
            description: t('packages.northern11.description'),
            location: "Sri Lanka",
            category: t('packages.northern11.category'),
        },
        {
            id: 3,
            image: package3,
            title: t('packages.meditation9.title'),
            duration: t('packages.meditation9.duration'),
            description: t('packages.meditation9.description'),
            price_usd: "1,000–1,200",
            location: "Sri Lanka",
            category: t('packages.meditation9.category'),
        },
        {
            id: 4,
            image: package4,
            title: t('packages.classic9.title'),
            duration: t('packages.classic9.duration'),
            description: t('packages.classic9.description'),
            location: "Sri Lanka",
            category: t('packages.classic9.category'),
        },
    ];

    // === FEATURED EXPERIENCES DATA (Translated) ===
    const featuredExperiences = [
        {
            id: 1,
            h2: t('featured.perahera.h2'),
            h3: t('featured.perahera.h3'),
            h4: t('featured.perahera.h4'),
            paragraph: t('featured.perahera.paragraph', { interpolation: { escapeValue: false } }),
            image: esalaPeraharaImage,
            label: label1,
            buttonColor: "#009CE6",
            buttonHover: "#007bb5",
            buttonText: t('featured.perahera.button'),
        },
        {
            id: 2,
            h2: t('featured.safari.h2'),
            h3: t('featured.safari.h3'),
            paragraph: t('featured.safari.paragraph', { interpolation: { escapeValue: false } }),
            image: safariImage,
            label: label2,
            buttonColor: "#96BA33",
            buttonHover: "#7a9a2a",
            buttonText: t('featured.safari.button'),
        },
    ];

    // === SLIDER LOGIC ===
    useEffect(() => {
        const handleResize = () => {
            setIsMd(window.innerWidth >= 768 && window.innerWidth <= 1024);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const nextSlide = () => setActivePackage((prev) => (prev + 1) % packages.length);
    const prevSlide = () => setActivePackage((prev) => (prev - 1 + packages.length) % packages.length);
    const slideDistance = isMd ? 180 : 340;

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://elfsightcdn.com/platform.js';
        script.async = true;
        document.body.appendChild(script);
        return () => {
            if (document.body.contains(script)) document.body.removeChild(script);
        };
    }, []);

    return (
        <div className="bg-black">
            {/* HERO */}
            <HomeHero />

            {/* RECOMMENDATIONS SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-16"
            >
                <div className="container mx-auto px-4">
                    <div className="flex w-full flex-col items-center">
                        <TitleTwoBorder mainTitle={t('sections.recommendations.title')} subTitle={t('sections.recommendations.subtitle')} />
                        <div className="relative mt-12 w-full lg:mt-16">
                            <Swiper
                                modules={[Navigation, Autoplay]}
                                spaceBetween={16}
                                slidesPerView={1.2}
                                centeredSlides={false}
                                loop={true}
                                autoplay={{ delay: 4000, disableOnInteraction: false }}
                                navigation={{ prevEl: '.swiper-rec-prev', nextEl: '.swiper-rec-next' }}
                                breakpoints={{
                                    640: { slidesPerView: 2.2 },
                                    768: { slidesPerView: 2.5 },
                                    1024: { slidesPerView: 3.2 },
                                    1280: { slidesPerView: 4 },
                                }}
                                className="pb-8"
                            >
                                {recommendations.map((rec) => (
                                    <SwiperSlide key={rec.name}>
                                        <div className="group relative h-[520px] w-full overflow-hidden rounded-[15px] transition-transform duration-300 hover:scale-105 md:h-[480px] lg:h-[520px] xl:h-[650px] cursor-pointer">
                                            <img src={rec.image} alt={rec.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                                            <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center px-4 lg:bottom-8 xl:bottom-10">
                                                <h4 className="font-redHatDisplay text-4xl font-black text-white drop-shadow-lg md:text-3xl lg:text-5xl xl:text-6xl">{rec.label}</h4>
                                                <h5 className="mt-1 font-redHatDisplay text-2xl font-medium text-white drop-shadow-md md:text-2xl lg:mt-2 lg:text-3xl xl:text-3xl">{rec.name}</h5>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                            <div className="swiper-rec-prev !left-2 top-1/2 -translate-y-1/2 !text-[#009CE6] after:!text-3xl cursor-pointer md:!left-4 lg:after:!text-4xl"></div>
                            <div className="swiper-rec-next !right-2 top-1/2 -translate-y-1/2 !text-[#009CE6] after:!text-3xl cursor-pointer md:!right-4 lg:after:!text-4xl"></div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* EXCURSIONS SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black"
            >
                <div className="container mx-auto px-4 font-redHatDisplay text-white">
                    <motion.div variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#009CE6]"></div>
                        <h1 className="text-[20px] font-semibold uppercase md:text-[30px] lg:text-[40px]">{t('sections.excursions.title')}</h1>
                    </motion.div>
                    <motion.div variants={textVariants} className="mt-2 justify-between lg:flex">
                        <div className="w-full font-semibold text-[#AEAEAE] lg:w-[50%] lg:text-[18px]">
                            {t('sections.excursions.description')}
                        </div>
                        <div className="mt-5 justify-end lg:mt-0 lg:flex lg:w-[50%]">
                            <a href="/tour" className="flex w-auto items-center justify-center gap-2 rounded-full bg-white px-5 py-2 text-[18px] font-semibold text-black transition hover:bg-gray-200 lg:text-[20px]">{t('buttons.viewAll')}</a>
                        </div>
                    </motion.div>
                    <motion.div variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>
                    <div className="justify-between gap-14 lg:flex">
                        <motion.div variants={scaleUp} className="relative mb-10 h-[400px] w-full lg:mb-0 lg:h-[550px] lg:w-[50%]">
                            <img src={excursionImage} alt="Excursion" className="h-full w-full rounded-lg object-cover shadow-2xl" loading="lazy" />
                        </motion.div>
                        <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} className="w-full space-y-4 lg:w-[60%]">
                            {excursions.map((service, index) => (
                                <motion.div key={index} variants={accordionItem} className="cursor-pointer rounded-lg border border-gray-600 bg-[#1a1a1a] p-4 transition hover:border-[#009CE6]" onClick={() => toggleService(index)}>
                                    <h2 className="flex items-center justify-between text-[16px] font-semibold lg:text-[20px]">
                                        {service.title}
                                        <span className="text-[25px] text-[#96BA33]">{activeIndex === index ? '−' : '+'}</span>
                                    </h2>
                                    {activeIndex === index && <p className="mt-2 text-[14px] text-[#C2C2C2] lg:text-[16px]">{service.des}</p>}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* FEATURED EXPERIENCES SECTION */}

            <div className="container mx-auto px-4">
                {/* MOBILE */}
                <div className="block sm:hidden space-y-20">
                    {featuredExperiences.map((exp) => (
                        <motion.div key={exp.id} variants={textVariants} className="flex flex-col items-center text-center">
                            <h2 className="font-redHatDisplay text-xl font-medium uppercase leading-[64px] text-[#009CE6]">{exp.h2}</h2>
                            <h3 className="font-redHatDisplay text-3xl font-bold uppercase leading-[64px] text-white">{exp.h3}</h3>
                            {exp.h4 && <h4 className="font-redHatDisplay text-base font-medium leading-[40px] text-[#96BA33]">{exp.h4}</h4>}
                            <div className="relative mt-6 h-[500px] w-full rounded-[30px] border-[3px] border-[#595959] overflow-hidden">
                                <img src={exp.image} alt={exp.h3} className="h-full w-full object-cover" loading="lazy" />
                                {exp.label && (
                                    <img src={exp.label} alt="Label" className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-[120px] w-auto object-contain" />
                                )}
                            </div>
                            <p className="mt-6 px-4 text-sm font-medium leading-7 text-white/90" dangerouslySetInnerHTML={{ __html: exp.paragraph }} />
                            <a href={exp.id === 1 ? "/events" : "/gallery"} target="_blank" rel="noopener noreferrer">
                                <button
                                    className="mt-6 rounded-full px-6 py-3 text-lg font-semibold text-white transition"
                                    style={{ backgroundColor: exp.buttonColor }}
                                    onMouseEnter={(e) => (e.target.style.backgroundColor = exp.buttonHover)}
                                    onMouseLeave={(e) => (e.target.style.backgroundColor = exp.buttonColor)}
                                >
                                    {exp.buttonText}
                                </button>
                            </a>
                        </motion.div>
                    ))}
                </div>

                {/* DESKTOP */}
                <div className="hidden sm:block space-y-20">
                    {/* Card 1 */}
                    <motion.div variants={textVariants} className="flex flex-col items-center justify-between md:flex-row">
                        <div className="flex w-full flex-col items-center md:w-[50%]">
                            <h2 className="font-redHatDisplay text-xl font-medium uppercase leading-[64px] text-[#009CE6] lg:text-3xl xl:text-[2.5rem]">{featuredExperiences[0].h2}</h2>
                            <h3 className="text-center font-redHatDisplay text-3xl font-bold uppercase leading-[64px] text-white md:text-4xl lg:text-5xl xl:text-[3.75rem]">{featuredExperiences[0].h3}</h3>
                            <h4 className="font-redHatDisplay text-base font-medium leading-[40px] text-[#96BA33] lg:text-lg lg:leading-[64px] xl:text-2xl">{featuredExperiences[0].h4}</h4>
                            <p className="mt-4 px-5 text-center font-redHatDisplay text-sm font-medium leading-8 text-white/90 md:px-0 md:leading-normal lg:mt-5 lg:w-[439px] lg:text-lg xl:w-[590px] xl:text-xl" dangerouslySetInnerHTML={{ __html: featuredExperiences[0].paragraph }} />
                            <a href="/events" target="_blank" rel="noopener noreferrer">
                                <button className="mt-7 rounded-full bg-[#009CE6] px-6 py-3 text-lg font-semibold text-white transition hover:bg-[#007bb5]">
                                    {featuredExperiences[0].buttonText}
                                </button>
                            </a>
                        </div>
                        <div className="relative mt-8 h-[600px] w-full rounded-[30px] border-[3px] border-[#595959] md:mt-0 md:w-[328px] lg:h-[700px] lg:w-[500px] xl:h-[800px] xl:w-[675px]">
                            <div className="h-full w-full overflow-hidden rounded-[27px]">
                                <img src={esalaPeraharaImage} alt="Perahera" className="h-full w-full object-cover" loading="lazy" />
                            </div>
                            <img src={label1} alt="Label" className="absolute -bottom-1 -left-8 hidden h-[150px] w-auto object-contain md:block xl:-left-12 xl:h-[200px]" />
                        </div>
                    </motion.div>

                    {/* Card 2 */}
                    <motion.div variants={textVariants} className="flex flex-col-reverse items-center justify-between md:flex-row">
                        <div className="relative mt-8 h-[600px] w-full rounded-[30px] border-[3px] border-[#595959] md:mt-0 md:w-[328px] lg:h-[700px] lg:w-[500px] xl:h-[800px] xl:w-[675px]">
                            <div className="h-full w-full overflow-hidden rounded-[27px]">
                                <img src={safariImage} alt="Safari" className="h-full w-full object-cover" loading="lazy" />
                            </div>
                            <img src={label2} alt="Label" className="absolute -right-8 bottom-0 hidden h-[150px] w-auto object-contain md:block xl:-right-12 xl:h-[200px]" />
                        </div>
                        <div className="flex w-full flex-col items-center md:w-[50%]">
                            <h2 className="font-redHatDisplay text-xl font-medium uppercase leading-[64px] text-[#009CE6] lg:text-3xl xl:text-[2.5rem]">{featuredExperiences[1].h2}</h2>
                            <h3 className="text-center font-redHatDisplay text-3xl font-bold uppercase leading-[64px] text-white md:text-4xl lg:text-5xl xl:text-[3.75rem]">{featuredExperiences[1].h3}</h3>
                            <p className="mt-4 px-5 text-center font-redHatDisplay text-sm font-medium leading-8 text-white/90 md:px-0 md:leading-normal lg:mt-5 lg:w-[439px] lg:text-xl xl:w-[590px]" dangerouslySetInnerHTML={{ __html: featuredExperiences[1].paragraph }} />
                            <a href="/gallery" target="_blank" rel="noopener noreferrer">
                                <button className="mt-7 rounded-full bg-[#96BA33] px-6 py-3 text-lg font-semibold text-white transition hover:bg-[#7a9a2a]">
                                    {featuredExperiences[1].buttonText}
                                </button>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </div>


            {/* PACKAGES SLIDER SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-10"
            >
                <div className="container mx-auto px-4 relative z-10 flex flex-col items-center">
                    <motion.div variants={textVariants} className="flex w-full items-center gap-10">
                        <div className="h-[5px] w-full bg-white/60"></div>
                        <h2 className="whitespace-nowrap font-konkhmer text-3xl font-bold uppercase text-white md:text-4xl lg:text-5xl">
                            <span style={{ background: 'linear-gradient(to right, #009CE6, #96BA33)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                {t('sections.packages.title')}
                            </span>
                        </h2>
                    </motion.div>

                    <div className="relative  flex h-[600px] w-full items-center justify-center md:h-[700px] lg:mt-20 lg:h-[840px] xl:mt-20 xl:h-[980px]">
                        <button onClick={prevSlide} className="absolute left-4 z-30 rounded-full border-2 border-white/50 bg-transparent p-3 backdrop-blur-sm transition hover:bg-white/10">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>
                        <button onClick={nextSlide} className="absolute right-4 z-30 rounded-full border-2 border-white/50 bg-transparent p-3 backdrop-blur-sm transition hover:bg-white/10">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </button>

                        <div className="relative flex h-full w-full items-center justify-center">
                            {packages.map((pkg, idx) => {
                                const diff = (idx - activePackage + packages.length) % packages.length;
                                let transform = '', zIndex = 0, opacity = 0;
                                if (diff === 0) { transform = 'translateX(0) scale(1)'; zIndex = 20; opacity = 1; }
                                else if (diff === 1 || (activePackage === packages.length - 1 && idx === 0)) { transform = `translateX(${slideDistance}px)`; zIndex = 10; opacity = 1; }
                                else if (diff === packages.length - 1 || (activePackage === 0 && idx === packages.length - 1)) { transform = `translateX(-${slideDistance}px)`; zIndex = 10; opacity = 1; }
                                else { transform = 'translateX(0) scale(0)'; zIndex = 0; opacity = 0; }

                                return (
                                    <div key={pkg.id} className="absolute transition-all duration-700 ease-in-out" style={{ transform, zIndex, opacity }}>
                                        {diff === 0 ? (
                                            <div className="w-[255px] lg:w-[435px] xl:w-[570px]">
                                                <div className="mx-auto w-full overflow-hidden rounded-[20px] border border-[#009CE6]/50 bg-gradient-to-b from-[#1A6983]/80 to-[#0A3D50]/80 p-[22px] text-center text-white backdrop-blur-sm lg:p-[37px] xl:p-[50px]">
                                                    <img src={pkg.image} alt={pkg.title} className="h-[167px] w-full rounded-[20px] object-cover lg:h-[372px] xl:h-[500px]" loading="lazy" />
                                                    <h3 className="mt-5 font-redHatDisplay text-lg font-bold uppercase tracking-wider text-white lg:text-xl xl:text-2xl">{pkg.title}</h3>
                                                    <p className="mt-1 font-redHatDisplay text-lg font-medium lg:text-xl xl:text-2xl text-[#96BA33]">({pkg.duration})</p>
                                                    <p className="mx-auto mt-3 font-redHatDisplay text-xs leading-relaxed text-white lg:mt-4 lg:text-base xl:text-lg">{pkg.description}</p>
                                                </div>
                                                <a href="/tour" target="_blank" rel="noopener noreferrer">
                                                    <button className="mt-5 w-full rounded-[30px] bg-gradient-to-r from-[#009CE6] to-[#96BA33] px-5 py-3 font-redHatDisplay text-base font-extrabold text-white transition hover:opacity-90 lg:mt-7 lg:text-lg xl:text-xl">
                                                        {t('buttons.bookNow')}
                                                    </button>
                                                </a>
                                            </div>
                                        ) : (
                                            <div className="mx-auto hidden w-[200px] overflow-hidden rounded-[20px] md:block lg:w-[350px] xl:w-[470px]">
                                                <img src={pkg.image} alt={pkg.title} className="h-[200px] w-full object-cover lg:h-[372px] xl:h-[500px]" loading="lazy" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* GOOGLE REVIEWS SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-4"
            >
                <div className="container mx-auto px-4">
                    <motion.div variants={textVariants} className="flex flex-col items-center text-center">
                        <h2 className="font-konkhmer text-3xl font-bold uppercase text-white md:text-4xl lg:text-5xl">
                            <span style={{ background: 'linear-gradient(to right, #009CE6, #96BA33)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                {t('sections.reviews.title')}
                            </span>
                        </h2>
                        <p className="mt-2 font-inknut text-sm text-white/80 md:text-base lg:mt-3 lg:text-lg">
                            {t('sections.reviews.subtitle')}
                        </p>
                        <div className="mt-3 flex w-full max-w-md gap-2">
                            <div className="h-[3px] flex-1" style={{ background: '#009CE6' }}></div>
                            <div className="h-[3px] flex-1" style={{ background: '#96BA33' }}></div>
                        </div>
                    </motion.div>
                    <motion.div variants={scaleUp} className="mt-12 flex justify-center">
                        <div className="elfsight-app-8318f6b8-057e-4f1f-8fce-eaf664f73c0f w-full max-w-4xl" data-elfsight-app-lazy style={{ minHeight: '400px' }}></div>
                    </motion.div>
                </div>
            </motion.section>

            {/* CRAFTING YOUR JOURNEY SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black mt-16 overflow-hidden py-12"
            >
                <div className="relative w-full h-[60vh] md:h-[40vh] lg:h-[70vh] bg-cover bg-center flex items-center justify-center container">
                    <img src={aboutContactImage} alt="Crafting Your Journey" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/50" />
                    <motion.div variants={textVariants} className="relative z-10 flex flex-col items-center justify-center gap-4 sm:gap-6 px-4 sm:px-6 text-center max-w-5xl">
                        <h1 className="font-konkhmer font-bold text-[20px] sm:text-3xl lg:text-5xl text-white leading-tight">
                            {t('sections.crafting.title')}
                        </h1>
                        <p className="mt-2 text-white/90 max-w-2xl">
                            {t('sections.crafting.description')}
                        </p>
                        <a href="/contact-us" className="mt-4 inline-block rounded-full bg-[#009CE6] px-6 py-3 font-semibold text-white">
                            {t('buttons.contactUs')}
                        </a>
                    </motion.div>
                </div>
            </motion.section>
        </div>
    );
};

export default Home;